import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Button, message, Spin, Tag, Input, Form, Tabs } from 'antd'
import {
  SettingOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  BankOutlined,
  SaveOutlined,
  SearchOutlined
} from '@ant-design/icons'
import InstitutionUserService from '../../services/InstitutionUserService'
import RefundServiceApi from '../../services/RefundServiceApi'

const RefundConfiguration: React.FC = () => {
  const navigate = useNavigate()
  const [bidderAuto, setBidderAuto] = useState(false)
  const [bailiffAuto, setBailiffAuto] = useState(false)
  const [bankCode] = useState('40')
  const [bankName] = useState('Bank of Kigali')
  const [accountNumber, setAccountNumber] = useState('')
  const [accountName, setAccountName] = useState('')
  const [accountValidated, setAccountValidated] = useState(false)
  const [configLoading, setConfigLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [validating, setValidating] = useState(false)

  useEffect(() => {
    Promise.all([
      InstitutionUserService.getSystemSettingByKey('BIDDER_AUTO_REFUND_ENABLED')
        .then((res: { status: number, data?: { setting_value: string } }) => {
          if (res.status === 200 && res.data !== undefined && res.data !== null) {
            setBidderAuto(res.data.setting_value === 'true')
          }
        }),
      InstitutionUserService.getSystemSettingByKey('BAILIFF_AUTO_REFUND_ENABLED')
        .then((res: { status: number, data?: { setting_value: string } }) => {
          if (res.status === 200 && res.data !== undefined && res.data !== null) {
            setBailiffAuto(res.data.setting_value === 'true')
          }
        }),
      InstitutionUserService.getSystemSettingByKey('BAILIFF_REFUND_ACCOUNT_NUMBER')
        .then((res: { status: number, data?: { setting_value: string } }) => {
          if (res.status === 200 && res.data !== undefined && res.data !== null) {
            setAccountNumber(res.data.setting_value === 'N/A' ? '' : res.data.setting_value)
          }
        }),
      InstitutionUserService.getSystemSettingByKey('BAILIFF_REFUND_ACCOUNT_NAME')
        .then((res: { status: number, data?: { setting_value: string } }) => {
          if (res.status === 200 && res.data !== undefined && res.data !== null) {
            setAccountName(res.data.setting_value)
          }
        })
    ])
      .then(() => {
        setConfigLoading(false)
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load configurations:', err)
        setBidderAuto(false)
        setBailiffAuto(false)
        setConfigLoading(false)
      })
  }, [])

  const handleUpdateSetting = async (
    key: 'BIDDER_AUTO_REFUND_ENABLED' | 'BAILIFF_AUTO_REFUND_ENABLED',
    checked: boolean
  ): Promise<void> => {
    setSaving(true)
    try {
      await InstitutionUserService.updateSystemSettingByKey(key, {
        setting_value: String(checked),
        updated_by: 'MINIJUST FINANCE'
      })
      if (key === 'BIDDER_AUTO_REFUND_ENABLED') {
        setBidderAuto(checked)
      } else {
        setBailiffAuto(checked)
      }
      const label = key === 'BIDDER_AUTO_REFUND_ENABLED' ? 'Bidder' : 'Bailiff'
      void message.success(
        `${label} auto-refund processing has been ${checked ? 'enabled' : 'disabled'}.`
      )
    } catch (err: unknown) {
      const errorObj = err as { response?: { status?: number }, message?: string }
      // eslint-disable-next-line no-console
      console.error(`Failed to update configuration for ${key}:`, err)

      if (errorObj.response?.status === 404) {
        try {
          const desc = key === 'BIDDER_AUTO_REFUND_ENABLED'
            ? 'Enable or disable automated refund processing for bidders'
            : 'Enable or disable automated refund processing for bailiffs'

          await InstitutionUserService.createSystemSetting({
            setting_key: key,
            setting_value: String(checked),
            setting_category: 'REFUND',
            setting_description: desc,
            data_type: 'boolean',
            created_by: 'MINIJUST FINANCE'
          })
          if (key === 'BIDDER_AUTO_REFUND_ENABLED') {
            setBidderAuto(checked)
          } else {
            setBailiffAuto(checked)
          }
          const label = key === 'BIDDER_AUTO_REFUND_ENABLED' ? 'Bidder' : 'Bailiff'
          void message.success(
            `${label} auto-refund processing has been ${checked ? 'enabled' : 'disabled'}.`
          )
        } catch (createErr) {
          // eslint-disable-next-line no-console
          console.error('Failed to create setting:', createErr)
          void message.error('Failed to save configuration.')
        }
      } else {
        const msg = errorObj.message ?? 'Failed to update configuration.'
        void message.error(msg)
      }
    } finally {
      setSaving(false)
    }
  }

  /**
   * Validate account number via the refund microservice (through the backend proxy - see
   * RefundServiceApi.validateBankAccount), not the old Node-native /system-settings/validate-bk-account.
   * Auto-fills Account Name from display_name.
   */
  const handleValidateAccount = async (): Promise<void> => {
    if (accountNumber.trim() === '') {
      void message.error('Please enter an account number to validate.')
      return
    }
    setAccountValidated(false)
    setValidating(true)
    try {
      const res: any = await RefundServiceApi.validateBankAccount({
        accountNumber: accountNumber.trim(),
        bankCode
      })
      if (res?.success === true) {
        const displayName: string = (res.display_name as string | undefined) ?? ''
        if (displayName !== '') {
          setAccountName(displayName)
          setAccountValidated(true)
          void message.success(`Account verified: ${displayName}`)
        } else {
          void message.warning('Account found but no name was returned. You may enter the name manually.')
          setAccountValidated(true)
        }
      } else {
        void message.error((res?.failure_message as string | undefined) ?? 'Account validation failed. Please check the account number.')
      }
    } catch (err: any) {
      void message.error(
        (err?.message as string | undefined) ?? 'Account validation failed. Please check the account number.'
      )
    } finally {
      setValidating(false)
    }
  }

  /** Save all settings to DB after account has been validated */
  const handleSaveAccount = async (): Promise<void> => {
    if (!accountValidated) {
      void message.error('Please validate the account number before saving.')
      return
    }
    if (accountName.trim() === '') {
      void message.error('Account name is required. Please validate the account first.')
      return
    }
    setSaving(true)
    try {
      const updates = [
        { key: 'BAILIFF_REFUND_BANK_CODE', val: bankCode },
        { key: 'BAILIFF_REFUND_BANK_NAME', val: bankName },
        { key: 'BAILIFF_REFUND_ACCOUNT_NUMBER', val: accountNumber.trim() },
        { key: 'BAILIFF_REFUND_ACCOUNT_NAME', val: accountName.trim() }
      ]
      for (const item of updates) {
        try {
          await InstitutionUserService.updateSystemSettingByKey(item.key, {
            setting_value: item.val,
            updated_by: 'MINIJUST FINANCE'
          })
        } catch (err: any) {
          if (err.response?.status === 404) {
            await InstitutionUserService.createSystemSetting({
              setting_key: item.key,
              setting_value: item.val,
              setting_category: 'REFUND',
              setting_description: `Default ${item.key.replace('BAILIFF_REFUND_', '').toLowerCase().replace(/_/g, ' ')} for bailiff refunds`,
              data_type: 'string',
              created_by: 'MINIJUST FINANCE'
            })
          } else {
            throw err
          }
        }
      }
      void message.success('Bailiff bank account details saved successfully.')
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('Failed to save bank account settings:', err)
      void message.error('Failed to save configuration settings.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => {
            navigate('/finance-dashboard')
          }}
          style={{ fontSize: '16px' }}
        />
        <h2 style={{ margin: 0, fontWeight: 700, color: '#1e293b' }}>
          Refund Payout Configuration
        </h2>
      </div>

      <Spin spinning={configLoading}>
        <Card
          bordered={false}
          style={{
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(226, 232, 240, 0.8)',
            boxShadow: '0 10px 30px -5px rgba(15, 23, 42, 0.05)',
            borderRadius: '16px',
            marginBottom: '24px'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div
                style={{
                  background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                  borderRadius: '12px',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#475569',
                  fontSize: '20px'
                }}
              >
                <SettingOutlined spin={saving} />
              </div>
              <div style={{ textAlign: 'left' }}>
                <h3 style={{ margin: 0, fontWeight: 600, color: '#0f172a' }}>
                  Refund Preferences
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                  Configure whether bidder or bailiff refunds trigger automatically on auction close.
                </p>
              </div>
            </div>

            <Tabs defaultActiveKey="1" className="custom-tabs" style={{ marginTop: '8px' }}>
              <Tabs.TabPane tab={<span style={{ fontWeight: 600 }}>Automation Preferences</span>} key="1">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', paddingTop: '16px' }}>
                  {/* 1. Bidder Config Options */}
                  <div
                    style={{
                      background: '#f8fafc',
                      borderRadius: '12px',
                      padding: '20px',
                      border: '1.5px solid #f1f5f9',
                      textAlign: 'left'
                    }}
                  >
                    <h4 style={{ margin: '0 0 12px 0', fontWeight: 600, color: '#334155' }}>
                      Bidders Refund Preference
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <Card
                        hoverable
                        onClick={() => {
                          void handleUpdateSetting('BIDDER_AUTO_REFUND_ENABLED', true)
                        }}
                        style={{
                          borderRadius: '8px',
                          border: bidderAuto ? '2px solid #37762f' : '1px solid #e2e8f0',
                          background: bidderAuto ? '#f0fdf4' : '#ffffff',
                          cursor: 'pointer'
                        }}
                        bodyStyle={{ padding: '14px 16px' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                          <CheckCircleOutlined
                            style={{
                              fontSize: '18px',
                              color: bidderAuto ? '#16a34a' : '#94a3b8',
                              marginTop: '3px'
                            }}
                          />
                          <div>
                            <span style={{ fontWeight: 600, color: '#1e293b', display: 'block' }}>
                              Automated Payout (Bidders)
                            </span>
                            <span style={{ fontSize: '12px', color: '#64748b', display: 'block', marginTop: '2px' }}>
                              Bidder refunds execute automatically upon closing the auction.
                            </span>
                          </div>
                        </div>
                      </Card>

                      <Card
                        hoverable
                        onClick={() => {
                          void handleUpdateSetting('BIDDER_AUTO_REFUND_ENABLED', false)
                        }}
                        style={{
                          borderRadius: '8px',
                          border: !bidderAuto ? '2px solid #37762f' : '1px solid #e2e8f0',
                          background: !bidderAuto ? '#f8fafc' : '#ffffff',
                          cursor: 'pointer'
                        }}
                        bodyStyle={{ padding: '14px 16px' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                          <CheckCircleOutlined
                            style={{
                              fontSize: '18px',
                              color: !bidderAuto ? '#16a34a' : '#94a3b8',
                              marginTop: '3px'
                            }}
                          />
                          <div>
                            <span style={{ fontWeight: 600, color: '#1e293b', display: 'block' }}>
                              Manual Review (Bidders)
                            </span>
                            <span style={{ fontSize: '12px', color: '#64748b', display: 'block', marginTop: '2px' }}>
                              Bidder refunds are generated as PENDING and require manual review.
                            </span>
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>

                  {/* 2. Bailiff Config Options */}
                  <div
                    style={{
                      background: '#f8fafc',
                      borderRadius: '12px',
                      padding: '20px',
                      border: '1.5px solid #f1f5f9',
                      textAlign: 'left'
                    }}
                  >
                    <h4 style={{ margin: '0 0 12px 0', fontWeight: 600, color: '#334155' }}>
                      Bailiffs Refund Preference
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <Card
                        hoverable
                        onClick={() => {
                          void handleUpdateSetting('BAILIFF_AUTO_REFUND_ENABLED', true)
                        }}
                        style={{
                          borderRadius: '8px',
                          border: bailiffAuto ? '2px solid #37762f' : '1px solid #e2e8f0',
                          background: bailiffAuto ? '#f0fdf4' : '#ffffff',
                          cursor: 'pointer'
                        }}
                        bodyStyle={{ padding: '14px 16px' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                          <CheckCircleOutlined
                            style={{
                              fontSize: '18px',
                              color: bailiffAuto ? '#16a34a' : '#94a3b8',
                              marginTop: '3px'
                            }}
                          />
                          <div>
                            <span style={{ fontWeight: 600, color: '#1e293b', display: 'block' }}>
                              Automated Payout (Bailiffs)
                            </span>
                            <span style={{ fontSize: '12px', color: '#64748b', display: 'block', marginTop: '2px' }}>
                              Bailiff refunds execute automatically upon closing the auction.
                            </span>
                          </div>
                        </div>
                      </Card>

                      <Card
                        hoverable
                        onClick={() => {
                          void handleUpdateSetting('BAILIFF_AUTO_REFUND_ENABLED', false)
                        }}
                        style={{
                          borderRadius: '8px',
                          border: !bailiffAuto ? '2px solid #37762f' : '1px solid #e2e8f0',
                          background: !bailiffAuto ? '#f8fafc' : '#ffffff',
                          cursor: 'pointer'
                        }}
                        bodyStyle={{ padding: '14px 16px' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                          <CheckCircleOutlined
                            style={{
                              fontSize: '18px',
                              color: !bailiffAuto ? '#16a34a' : '#94a3b8',
                              marginTop: '3px'
                            }}
                          />
                          <div>
                            <span style={{ fontWeight: 600, color: '#1e293b', display: 'block' }}>
                              Manual Review (Bailiffs)
                            </span>
                            <span style={{ fontSize: '12px', color: '#64748b', display: 'block', marginTop: '2px' }}>
                              Bailiff refunds are generated as PENDING and require manual review.
                            </span>
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 4px 0 4px' }}>
                    <InfoCircleOutlined style={{ color: '#0284c7' }} />
                    <span style={{ fontSize: '12px', color: '#0284c7', fontWeight: 500 }}>
                      Status: Bidders &mdash;{' '}
                      <Tag color={bidderAuto ? 'green' : 'blue'}>
                        {bidderAuto ? 'AUTO' : 'REVIEW'}
                      </Tag>{' '}
                      | Bailiffs &mdash;{' '}
                      <Tag color={bailiffAuto ? 'green' : 'blue'}>
                        {bailiffAuto ? 'AUTO' : 'REVIEW'}
                      </Tag>
                    </span>
                  </div>
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane tab={<span style={{ fontWeight: 600 }}>Default Payout Details</span>} key="2">
                <div style={{ paddingTop: '16px' }}>
                  {/* Bailiff Bank Account Configuration */}
                  <div
                    style={{
                      background: '#f8fafc',
                      borderRadius: '12px',
                      padding: '20px',
                      border: '1.5px solid #f1f5f9',
                      textAlign: 'left'
                    }}
                  >
                    <h4 style={{ margin: '0 0 4px 0', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <BankOutlined style={{ color: '#37762f' }} />
                      Default Bailiff Bank Account (BK)
                    </h4>
                    <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#64748b' }}>
                      Enter the BK account number and click <strong>Validate &amp; Save</strong>. The account holder name will be verified
                      via the BK API before saving.
                    </p>

                    <Form layout="vertical">
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                        {/* Read-only Bank Code */}
                        <Form.Item label={
                          <span style={{ fontWeight: 600, color: '#475569' }}>
                            Bank Code <Tag color="blue" style={{ marginLeft: '6px', fontSize: '10px' }}>Read-only</Tag>
                          </span>
                        } style={{ margin: 0 }}>
                          <Input
                            value={bankCode}
                            readOnly
                            style={{ borderRadius: '8px', background: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' }}
                            size="large"
                          />
                        </Form.Item>

                        {/* Read-only Bank Name */}
                        <Form.Item label={
                          <span style={{ fontWeight: 600, color: '#475569' }}>
                            Bank Name <Tag color="blue" style={{ marginLeft: '6px', fontSize: '10px' }}>Read-only</Tag>
                          </span>
                        } style={{ margin: 0 }}>
                          <Input
                            value={bankName}
                            readOnly
                            style={{ borderRadius: '8px', background: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' }}
                            size="large"
                          />
                        </Form.Item>

                        {/* Editable Account Number */}
                        <Form.Item label={<span style={{ fontWeight: 600, color: '#475569' }}>Account Number</span>} required style={{ margin: 0 }}>
                          <Input
                            placeholder="e.g. 100012345678"
                            value={accountNumber}
                            onChange={(e) => { setAccountNumber(e.target.value) }}
                            style={{ borderRadius: '8px' }}
                            size="large"
                          />
                        </Form.Item>

                        {/* Account Name — auto-filled from BK validation */}
                        <Form.Item
                          label={
                            <span style={{ fontWeight: 600, color: '#475569' }}>
                              Account Name
                              {accountValidated && (
                                <Tag color="green" style={{ marginLeft: '8px', fontSize: '10px' }}>
                                  ✓ Verified
                                </Tag>
                              )}
                            </span>
                          }
                          style={{ margin: 0 }}
                        >
                          <Input
                            placeholder="Will be auto-filled after validation"
                            value={accountName}
                            onChange={(e) => {
                              setAccountName(e.target.value)
                              setAccountValidated(false)
                            }}
                            style={{
                              borderRadius: '8px',
                              borderColor: accountValidated ? '#16a34a' : undefined,
                              background: accountValidated ? '#f0fdf4' : undefined
                            }}
                            size="large"
                          />
                        </Form.Item>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '4px' }}>
                        <Button
                          icon={<SearchOutlined />}
                          loading={validating}
                          onClick={() => { void handleValidateAccount() }}
                          style={{
                            borderRadius: '8px',
                            height: '40px',
                            fontWeight: 600
                          }}
                        >
                          Validate Account
                        </Button>
                        <Button
                          type="primary"
                          icon={<SaveOutlined />}
                          loading={saving}
                          disabled={!accountValidated}
                          onClick={() => { void handleSaveAccount() }}
                          style={{
                            borderRadius: '8px',
                            background: accountValidated ? '#37762f' : undefined,
                            borderColor: accountValidated ? '#37762f' : undefined,
                            height: '40px',
                            fontWeight: 600
                          }}
                        >
                          Save Settings
                        </Button>
                      </div>
                    </Form>
                  </div>
                </div>
              </Tabs.TabPane>
            </Tabs>
          </div>
        </Card>
      </Spin>
    </div>
  )
}

export default RefundConfiguration
